"use client";

import { UserRound } from "lucide-react";
import { useState } from "react";
import type { ResolvedMediaTag } from "@/actions/post";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { cn } from "@/lib/utils";

/**
 * Read-only Instagram-style people-tag overlay for a feed media item. A person
 * badge in the lower LEFT corner (the lower right belongs to the sound toggle)
 * toggles the name pills so the feed stays uncluttered until tapped.
 * Lives as a sibling of the media button (pointer-events-none base layer, with
 * only the badge/pills interactive) so taps elsewhere still open the viewer.
 */
export function MediaTagDots({
  tags,
  className,
  badgeClassName,
}: {
  tags: ResolvedMediaTag[];
  className?: string;
  badgeClassName?: string;
}) {
  const [open, setOpen] = useState(false);

  if (tags.length === 0) {
    return null;
  }

  return (
    <div className={cn("pointer-events-none absolute inset-0 z-10", className)}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          setOpen((value) => !value);
        }}
        className={cn(
          "pointer-events-auto absolute bottom-3 left-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition-[background-color,transform] duration-200 ease-out-strong hover:bg-black/70 active:scale-90",
          badgeClassName,
          open && "bg-black/75",
        )}
        aria-label={open ? "Hide tagged people" : "Show tagged people"}
      >
        <UserRound className="h-4 w-4 fill-current" />
      </button>

      {open
        ? tags.map((tag) => (
            <BackAwareLink
              key={tag.userId}
              href={tag.handle ? `/profile/${tag.handle}` : "#"}
              onClick={(event) => event.stopPropagation()}
              className="pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${tag.x * 100}%`, top: `${tag.y * 100}%` }}
            >
              <span className="flex items-center gap-1.5 rounded-full bg-black/75 py-1 pr-2.5 pl-1 text-xs text-white shadow-lg backdrop-blur-md">
                <Avatar className="h-5 w-5">
                  <AvatarImage src={tag.avatarUrl ?? undefined} />
                  <AvatarFallback className="bg-white/20 text-[9px] text-white">
                    {tag.displayName.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="max-w-[140px] truncate font-medium">
                  {tag.displayName}
                </span>
              </span>
            </BackAwareLink>
          ))
        : null}
    </div>
  );
}
